/**
 * terrain.ts — chunked heightfield terrain with discrete LOD.
 *
 * The 5 km map is split into CHUNKS x CHUNKS tiles; each tile prebuilds three
 * geometry resolutions and swaps them by camera distance. Vertex colors come
 * from the biome layer stack (biomes.ts), sampled against the sim
 * Heightfield so the render mesh and sim queries always agree.
 */
import * as THREE from "three";
import { Heightfield } from "../../sim/pcg";
import { sampleGround, groundColorToThree } from "./biomes";

const CANONICAL_SIZE = 5000;
/** Chunk grid: 10x10 at the canonical scale, 5x5 for skirmish maps. */
const CHUNKS_FOR_SIZE = (size: number) => (size > 1500 ? 10 : 5);
const LOD_SEGMENTS = [88, 44, 22];
/** LOD ring distances scale with the map: <d0: LOD0, <d1: LOD1, else LOD2. */
const LOD_DIST_FOR_SIZE = (size: number): [number, number] => {
  const s = Math.min(1, size / CANONICAL_SIZE);
  return [850 * s, 1900 * s];
};

interface Chunk {
  mesh: THREE.Mesh;
  cx: number;
  cz: number;
  lod: number;
  geometries: THREE.BufferGeometry[];
}

export interface TerrainOpts {
  material?: THREE.Material;
  /** Chunk meshes receive sun shadows. Default false (perf for the 5 km preview). */
  receiveShadow?: boolean;
}

export class Terrain {
  readonly group = new THREE.Group();
  /** Number of terrain chunk meshes (5x5 for skirmish maps, 10x10 canonical). */
  readonly chunkCount: number;
  private readonly chunks: Chunk[] = [];
  private readonly field: Heightfield;
  private readonly chunkSize: number;
  private readonly lodDist: [number, number];
  private lastLodCX = 1e9;
  private lastLodCZ = 1e9;
  private readonly color = new THREE.Color();

  constructor(field: Heightfield, opts: TerrainOpts = {}) {
    this.field = field;
    const chunks = CHUNKS_FOR_SIZE(field.size);
    this.lodDist = LOD_DIST_FOR_SIZE(field.size);
    this.chunkSize = field.size / chunks;
    const material =
      opts.material ??
      new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1, metalness: 0 });
    for (let j = 0; j < chunks; j++) {
      for (let i = 0; i < chunks; i++) {
        const cx = -field.half + (i + 0.5) * this.chunkSize;
        const cz = -field.half + (j + 0.5) * this.chunkSize;
        const geometries = LOD_SEGMENTS.map((seg) => this.buildGeometry(cx, cz, seg));
        const mesh = new THREE.Mesh(geometries[2], material);
        mesh.frustumCulled = true;
        mesh.receiveShadow = opts.receiveShadow ?? false;
        this.group.add(mesh);
        this.chunks.push({ mesh, cx, cz, lod: -1, geometries });
      }
    }
    this.chunkCount = this.chunks.length;
  }

  private buildGeometry(cx: number, cz: number, seg: number): THREE.BufferGeometry {
    const half = this.chunkSize / 2;
    const verts = (seg + 1) * (seg + 1);
    const positions = new Float32Array(verts * 3);
    const colors = new Float32Array(verts * 3);
    const idx: number[] = [];
    let v = 0;
    for (let j = 0; j <= seg; j++) {
      for (let i = 0; i <= seg; i++) {
        const x = cx - half + (this.chunkSize * i) / seg;
        const z = cz - half + (this.chunkSize * j) / seg;
        const h = this.field.heightAt(x, z);
        positions[v * 3] = x;
        positions[v * 3 + 1] = h;
        positions[v * 3 + 2] = z;
        const slope = this.field.slopeAt(x, z);
        const g = sampleGround(this.field, x, z, h, slope);
        groundColorToThree(g.color, this.color);
        colors[v * 3] = this.color.r;
        colors[v * 3 + 1] = this.color.g;
        colors[v * 3 + 2] = this.color.b;
        v++;
      }
    }
    for (let j = 0; j < seg; j++) {
      for (let i = 0; i < seg; i++) {
        const a = j * (seg + 1) + i;
        const b = a + 1;
        const c = a + seg + 1;
        const d = c + 1;
        idx.push(a, c, b, b, c, d);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    geo.computeBoundingSphere();
    return geo;
  }

  /** Reassign LOD per chunk; call when the camera moves to a new chunk. */
  update(camera: THREE.Camera): void {
    const ccx = Math.floor((camera.position.x + this.field.half) / this.chunkSize);
    const ccz = Math.floor((camera.position.z + this.field.half) / this.chunkSize);
    if (ccx === this.lastLodCX && ccz === this.lastLodCZ) return;
    this.lastLodCX = ccx;
    this.lastLodCZ = ccz;
    for (const ch of this.chunks) {
      const d = Math.hypot(ch.cx - camera.position.x, ch.cz - camera.position.z);
      const lod = d < this.lodDist[0] ? 0 : d < this.lodDist[1] ? 1 : 2;
      if (lod !== ch.lod) {
        ch.lod = lod;
        ch.mesh.geometry = ch.geometries[lod];
      }
    }
  }

  /** Force a full LOD refresh (e.g. after teleporting the camera). */
  refresh(camera: THREE.Camera): void {
    this.lastLodCX = 1e9;
    this.update(camera);
  }

  dispose(): void {
    for (const ch of this.chunks) for (const g of ch.geometries) g.dispose();
    this.group.clear();
  }
}
